<template>
  <Loader v-if="loading"></Loader>
  <div>
    <div class="center-content">
      <Card class="w-full">
        <template v-slot:header> </template>
        <template v-slot:content>
          <div class="section-card upload-status-section">
            <div class="upload-status-header">
              <h3 class="section-title">File RCU caricati (mese per mese)</h3>
              <Button
                @click="fetchMonthlyPresence"
                label="Aggiorna"
                icon="pi pi-refresh"
                size="small"
                class="p-button-outlined"
                :loading="loadingMonthlyPresence"
              />
            </div>
            <p class="upload-status-hint">
              Verifica se i dati RCU elettrico e gas risultano presenti a sistema
              per ciascun mese, con data/ora dell’ultimo upload file.
            </p>
            <div v-if="loadingMonthlyPresence && !monthlyPresence.length" class="details-loading">
              Caricamento stato file RCU...
            </div>
            <div v-else-if="monthlyPresenceError" class="error-message">
              {{ monthlyPresenceError }}
            </div>
            <div v-else class="upload-status-table-wrap">
              <table class="upload-status-table">
                <thead>
                  <tr>
                    <th>Mese</th>
                    <th>Elettrico</th>
                    <th>Caricato il (EE)</th>
                    <th>Gas</th>
                    <th>Caricato il (Gas)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in monthlyPresence" :key="row.month">
                    <td>{{ row.label }}</td>
                    <td>
                      <span
                        :class="[
                          'presence-badge',
                          row.elettrico ? 'presence-yes' : 'presence-no',
                        ]"
                      >
                        {{ row.elettrico ? "Sì" : "No" }}
                      </span>
                    </td>
                    <td class="upload-datetime">
                      {{ formatUploadDateTime(row.elettrico_uploaded_at) }}
                    </td>
                    <td>
                      <span
                        :class="[
                          'presence-badge',
                          row.gas ? 'presence-yes' : 'presence-no',
                        ]"
                      >
                        {{ row.gas ? "Sì" : "No" }}
                      </span>
                    </td>
                    <td class="upload-datetime">
                      {{ formatUploadDateTime(row.gas_uploaded_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="date-selection-section">
            <h3 class="section-title">Seleziona Periodo</h3>
            <div class="date-selector-container">
              <div class="date-selector-row">
                <label for="monthYear">Seleziona Anno/Mese:</label>
                <Calendar
                  v-model="selectedDate"
                  view="month"
                  dateFormat="mm/yy"
                  :showIcon="true"
                  :showButtonBar="false"
                  class="custom-calendar"
                  panelClass="month-picker-panel"
                />
                <Button
                  @click="fetchRcuData"
                  label="Carica Dati RCU"
                  icon="pi pi-search"
                  class="load-data-button"
                  :disabled="!selectedDate"
                  :loading="loading"
                />
              </div>
            </div>
          </div>

          <div v-if="rcuData" class="rcu-data-container">
            <div class="section-card">
              <h3 class="section-title">Informazioni Generali</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Mese:</label>
                  <span>{{ rcuData.current_month }}</span>
                </div>
                <div class="info-item">
                  <label>Timestamp Check:</label>
                  <span>{{ rcuData.summary.check_timestamp }}</span>
                </div>
              </div>
            </div>

            <div class="section-card">
              <h3 class="section-title">Riepilogo</h3>
              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pods_analyzed }}
                  </div>
                  <div class="summary-label">POD Analizzati</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pods_with_fatturabile }}
                  </div>
                  <div class="summary-label">POD con Dati Fatturabile</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pods_without_fatturabile }}
                  </div>
                  <div class="summary-label">POD senza Dati Fatturabile</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pdrs_analyzed }}
                  </div>
                  <div class="summary-label">PDR Analizzati</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pdrs_with_fatturabile_gas }}
                  </div>
                  <div class="summary-label">PDR con Dati Fatturabile</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.pdrs_without_fatturabile_gas }}
                  </div>
                  <div class="summary-label">PDR senza Dati Fatturabile</div>
                </div>
                
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.active_pods_count }}
                  </div>
                  <div class="summary-label">POD Da Attivare</div>
                </div>
                <div class="summary-item">
                  <div class="summary-number">
                    {{ rcuData.summary.active_pdrs_count }}
                  </div>
                  <div class="summary-label">PDR Da Attivare</div>
                </div>
              </div>
            </div>

            <div class="section-card">
              <h3 class="section-title">Stato Tabelle RCU</h3>
              <div class="tables-container">
                <div
                  v-for="(tableData, tableName) in rcuData.rcu_tables"
                  :key="tableName"
                  class="table-status-card"
                >
                  <div class="table-header">
                    <h4>{{ tableName }}</h4>
                    <span
                      :class="getImportStatusClass(tableData.import_status)"
                    >
                      {{ tableData.import_status }}
                    </span>
                  </div>
                  <div class="table-details">
                    <div class="detail-row">
                      <span class="detail-label">Record Mese Corrente:</span>
                      <span class="detail-value">{{
                        tableData.current_month_records
                      }}</span>
                    </div>
                    <div v-if="tableData.error" class="detail-row error">
                      <span class="detail-label">Errore:</span>
                      <span class="detail-value">{{ tableData.error }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section-card">
              <h3 class="section-title">Analisi POD</h3>
              <div v-if="rcuData.pod_analysis.error" class="error-message">
                {{ rcuData.pod_analysis.error }}
              </div>
              <div v-else>
                <div class="pod-stats">
                  <div class="stat-item">
                    <label>Totale POD Mese Corrente:</label>
                    <span>{{
                      rcuData.pod_analysis.total_pods_current_month
                    }}</span>
                  </div>
                  <div class="stat-item">
                    <label>POD con Dati Fatturabile:</label>
                    <span>{{
                      rcuData.pod_analysis.pods_with_fatturabile_data
                    }}</span>
                  </div>
                  <div class="stat-item">
                    <label>POD senza Dati Fatturabile:</label>
                    <span>{{
                      rcuData.pod_analysis.pods_without_fatturabile_data
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="
                    loadingDetails ||
                    (rcuData.pod_analysis.pods_with_fatturabile_data || 0) > 0
                  "
                  class="pod-details-section"
                >
                  <h4>Dettagli POD con Dati Fatturabile</h4>
                  <div v-if="loadingDetails" class="details-loading">
                    Caricamento dettagli POD in corso...
                  </div>
                  <template v-else>
                    <div class="filters-container">
                      <div class="search-container">
                        <input
                          v-model="podSearchQuery"
                          type="text"
                          placeholder="Cerca POD..."
                          class="search-input"
                          @input="onPodSearchInput"
                        />
                      </div>
                      <div class="filter-container">
                        <Button
                          :label="
                            showOnlyMissingPod
                              ? 'Mostra Tutti'
                              : 'Solo con Mesi Mancanti'
                          "
                          :class="[
                            'filter-button',
                            { 'filter-active': showOnlyMissingPod },
                          ]"
                          @click="toggleMissingFilter('pod')"
                          icon="pi pi-filter"
                          size="small"
                        />
                      </div>
                    </div>
                    <Paginator
                      :rows="podDetailsRows"
                      :totalRecords="filteredPodDetailsList.length"
                      :first="podDetailsFirst"
                      :rowsPerPageOptions="[25, 50, 100]"
                      @page="onPodDetailsPage"
                    />
                    <div class="pod-table-container">
                      <table class="pod-table">
                        <thead>
                          <tr>
                            <th>Pod Code</th>
                            <th>Numero Mesi</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template
                            v-for="item in pagedPodDetails"
                            :key="item.id"
                          >
                            <tr
                              :class="[
                                'pod-row',
                                {
                                  'has-missing-months': hasMissingMonths(
                                    item.data
                                  ),
                                },
                              ]"
                              @click="togglePod(item.id)"
                            >
                              <td>
                                <i
                                  :class="[
                                    'fas',
                                    expandedPods[item.id]
                                      ? 'fa-chevron-down'
                                      : 'fa-chevron-right',
                                  ]"
                                  class="toggle-icon"
                                ></i>
                                {{ item.data.pod_code || item.id }}
                              </td>
                              <td>
                                {{
                                  item.data.months_present
                                    ? item.data.months_present.length
                                    : 0
                                }}
                              </td>
                            </tr>
                            <tr
                              v-if="expandedPods[item.id]"
                              class="months-detail-row"
                            >
                              <td colspan="2">
                                <div class="months-detail">
                                  <div
                                    v-if="
                                      getMonthsComplete(item.data).length > 0
                                    "
                                  >
                                    <h6>
                                      Analisi Mesi Fatturabile ({{
                                        formatPeriodRange(
                                          getMonthsComplete(item.data)
                                        )
                                      }}):
                                    </h6>
                                    <div class="months-list">
                                      <span
                                        v-for="monthData in getMonthsComplete(
                                          item.data
                                        )"
                                        :key="monthData.month"
                                        :class="[
                                          'month-tag',
                                          getMonthStatusClass(monthData.status),
                                        ]"
                                        :title="getMonthTooltip(monthData)"
                                      >
                                        {{ formatMonth(monthData.month) }}
                                      </span>
                                    </div>
                                    <div class="legend">
                                      <span class="legend-item">
                                        <span
                                          class="legend-color present"
                                        ></span>
                                        Presente in Fatturabile
                                      </span>
                                      <span class="legend-item">
                                        <span
                                          class="legend-color missing"
                                        ></span>
                                        Mancante in Fatturabile
                                      </span>
                                    </div>
                                  </div>
                                  <div v-else>
                                    <p class="no-data-text">
                                      Nessun dato fatturabile trovato per questo
                                      POD
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="section-card">
              <h3 class="section-title">POD e PDR Anomali</h3>
              <div class="active-stats">
                <div class="stat-item">
                  <label>POD Anomali:</label>
                  <span>{{ rcuData.active_pods?.count || 0 }}</span>
                </div>
                <div class="stat-item">
                  <label>PDR Anomali:</label>
                  <span>{{ rcuData.active_pdrs?.count || 0 }}</span>
                </div>
              </div>

              <div
                v-if="rcuData.active_pods && rcuData.active_pods.count > 0"
                class="active-section"
              >
                <h4>POD Attivi ({{ rcuData.active_pods.count }})</h4>
                <Paginator
                  :rows="podsRows"
                  :totalRecords="rcuData.active_pods.count"
                  :first="podsFirst"
                  :rowsPerPageOptions="[6, 12, 24, 48]"
                  @page="onPodsPage"
                />
                <div class="active-grid">
                  <div
                    v-for="pod in pagedActivePods"
                    :key="pod.id"
                    class="active-item"
                  >
                    <div class="code">{{ pod.code || "N/A" }}</div>
                    <div class="data-fine">
                      Data Fine: {{ pod.data_fine || "N/A" }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="rcuData.active_pdrs && rcuData.active_pdrs.count > 0"
                class="active-section"
              >
                <h4>PDR Attivi ({{ rcuData.active_pdrs.count }})</h4>
                <Paginator
                  :rows="pdrRows"
                  :totalRecords="rcuData.active_pdrs.count"
                  :first="pdrFirst"
                  :rowsPerPageOptions="[6, 12, 24, 48]"
                  @page="onPdrPage"
                />
                <div class="active-grid">
                  <div
                    v-for="pdr in pagedActivePdrs"
                    :key="pdr.id"
                    class="active-item"
                  >
                    <div class="code">{{ pdr.code || "N/A" }}</div>
                    <div class="data-fine">
                      Data Fine: {{ pdr.data_fine || "N/A" }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="
                  (!rcuData.active_pods || rcuData.active_pods.count === 0) &&
                  (!rcuData.active_pdrs || rcuData.active_pdrs.count === 0)
                "
                class="no-active-data"
              >
                <p>Nessun POD o PDR attivo trovato</p>
              </div>
            </div>

            <div class="section-card">
              <h3 class="section-title">Analisi PDR (Gas)</h3>
              <div v-if="rcuData.pdr_analysis.error" class="error-message">
                {{ rcuData.pdr_analysis.error }}
              </div>
              <div v-else>
                <div class="pod-stats">
                  <div class="stat-item">
                    <label>Totale PDR Mese Corrente:</label>
                    <span>{{
                      rcuData.pdr_analysis.total_pdrs_current_month
                    }}</span>
                  </div>
                  <div class="stat-item">
                    <label>PDR con Dati Fatturabile Gas:</label>
                    <span>{{
                      rcuData.pdr_analysis.pdrs_with_fatturabile_data
                    }}</span>
                  </div>
                  <div class="stat-item">
                    <label>PDR senza Dati Fatturabile Gas:</label>
                    <span>{{
                      rcuData.pdr_analysis.pdrs_without_fatturabile_data
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="
                    loadingDetails ||
                    (rcuData.pdr_analysis.pdrs_with_fatturabile_data || 0) > 0
                  "
                  class="pod-details-section"
                >
                  <h4>Dettagli PDR con Dati Fatturabile Gas</h4>
                  <div v-if="loadingDetails" class="details-loading">
                    Caricamento dettagli PDR in corso...
                  </div>
                  <template v-else>
                    <div class="filters-container">
                      <div class="search-container">
                        <input
                          v-model="pdrSearchQuery"
                          type="text"
                          placeholder="Cerca PDR..."
                          class="search-input"
                          @input="onPdrSearchInput"
                        />
                      </div>
                      <div class="filter-container">
                        <Button
                          :label="
                            showOnlyMissingPdr
                              ? 'Mostra Tutti'
                              : 'Solo con Mesi Mancanti'
                          "
                          :class="[
                            'filter-button',
                            { 'filter-active': showOnlyMissingPdr },
                          ]"
                          @click="toggleMissingFilter('pdr')"
                          icon="pi pi-filter"
                          size="small"
                        />
                      </div>
                    </div>
                    <Paginator
                      :rows="pdrDetailsRows"
                      :totalRecords="filteredPdrDetailsList.length"
                      :first="pdrDetailsFirst"
                      :rowsPerPageOptions="[25, 50, 100]"
                      @page="onPdrDetailsPage"
                    />
                    <div class="pod-table-container">
                      <table class="pod-table">
                        <thead>
                          <tr>
                            <th>PDR Code</th>
                            <th>Numero Mesi</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template
                            v-for="item in pagedPdrDetails"
                            :key="item.id"
                          >
                            <tr
                              :class="[
                                'pod-row',
                                {
                                  'has-missing-months': hasMissingMonths(
                                    item.data
                                  ),
                                },
                              ]"
                              @click="togglePdr(item.id)"
                            >
                              <td>
                                <i
                                  :class="[
                                    'fas',
                                    expandedPdrs[item.id]
                                      ? 'fa-chevron-down'
                                      : 'fa-chevron-right',
                                  ]"
                                  class="toggle-icon"
                                ></i>
                                {{ item.data.pdr_code || item.id }}
                              </td>
                              <td>
                                {{
                                  item.data.months_present
                                    ? item.data.months_present.length
                                    : 0
                                }}
                              </td>
                            </tr>
                            <tr
                              v-if="expandedPdrs[item.id]"
                              class="months-detail-row"
                            >
                              <td colspan="2">
                                <div class="months-detail">
                                  <div
                                    v-if="
                                      getMonthsComplete(item.data).length > 0
                                    "
                                  >
                                    <h6>
                                      Analisi Mesi Fatturabile Gas ({{
                                        formatPeriodRange(
                                          getMonthsComplete(item.data)
                                        )
                                      }}):
                                    </h6>
                                    <div class="months-list">
                                      <span
                                        v-for="monthData in getMonthsComplete(
                                          item.data
                                        )"
                                        :key="monthData.month"
                                        :class="[
                                          'month-tag',
                                          getMonthStatusClass(monthData.status),
                                        ]"
                                        :title="
                                          getMonthTooltip(monthData, 'Gas')
                                        "
                                      >
                                        {{ formatMonth(monthData.month) }}
                                      </span>
                                    </div>
                                    <div class="legend">
                                      <span class="legend-item">
                                        <span
                                          class="legend-color present"
                                        ></span>
                                        Presente in Fatturabile Gas
                                      </span>
                                      <span class="legend-item">
                                        <span
                                          class="legend-color missing"
                                        ></span>
                                        Mancante in Fatturabile Gas
                                      </span>
                                    </div>
                                  </div>
                                  <div v-else>
                                    <p class="no-data-text">
                                      Nessun dato fatturabile gas trovato per
                                      questo PDR
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="actions-section">
              <Button
                @click="fetchRcuData"
                label="Aggiorna Dati"
                icon="pi pi-refresh"
                class="refresh-button"
                :loading="loading"
              />
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, onMounted } from "vue";
import Service from "@/services/ask-caricamento-file/caricamento-file";
import Loader from "@/components/Loader.vue";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import Card from "primevue/card";
import Calendar from "primevue/calendar";
import Paginator from "primevue/paginator";
import moment from "moment";

export default {
  components: {
    Loader,
    Button,
    Card,
    Calendar,
    Paginator,
  },

  setup() {
    const toast = useToast();
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const loading = ref(false);
    const loadingDetails = ref(false);
    const loadingMonthlyPresence = ref(false);
    const monthlyPresence = ref([]);
    const monthlyPresenceError = ref(null);
    const rcuData = ref(null);
    const podSearchQuery = ref("");
    const pdrSearchQuery = ref("");
    const expandedPods = ref({});
    const expandedPdrs = ref({});
    const selectedDate = ref(new Date());
    const showOnlyMissingPod = ref(false);
    const showOnlyMissingPdr = ref(false);
    const monthsCompleteCache = new Map();

    // Paginazione POD/PDR attivi
    const podsFirst = ref(0);
    const podsRows = ref(12);
    const pdrFirst = ref(0);
    const pdrRows = ref(12);

    // Paginazione dettagli fatturabile
    const podDetailsFirst = ref(0);
    const podDetailsRows = ref(50);
    const pdrDetailsFirst = ref(0);
    const pdrDetailsRows = ref(50);

    const activePodsList = computed(
      () => rcuData.value?.active_pods?.pods || []
    );
    const activePdrsList = computed(
      () => rcuData.value?.active_pdrs?.pdrs || []
    );

    const pagedActivePods = computed(() => {
      const start = podsFirst.value;
      const end = podsFirst.value + podsRows.value;
      return activePodsList.value.slice(start, end);
    });

    const pagedActivePdrs = computed(() => {
      const start = pdrFirst.value;
      const end = pdrFirst.value + pdrRows.value;
      return activePdrsList.value.slice(start, end);
    });

    const onPodsPage = (event) => {
      podsFirst.value = event.first;
      podsRows.value = event.rows;
    };

    const onPdrPage = (event) => {
      pdrFirst.value = event.first;
      pdrRows.value = event.rows;
    };

    const onPodDetailsPage = (event) => {
      podDetailsFirst.value = event.first;
      podDetailsRows.value = event.rows;
    };

    const onPdrDetailsPage = (event) => {
      pdrDetailsFirst.value = event.first;
      pdrDetailsRows.value = event.rows;
    };

    const onPodSearchInput = () => {
      podDetailsFirst.value = 0;
    };

    const onPdrSearchInput = () => {
      pdrDetailsFirst.value = 0;
    };

    const detailsToList = (details) => {
      if (!details || typeof details !== "object") return [];
      return Object.keys(details).map((id) => ({
        id,
        data: details[id],
      }));
    };

    const filteredPodDetailsList = computed(() => {
      let list = detailsToList(
        rcuData.value?.pod_analysis?.fatturabile_details
      );

      if (podSearchQuery.value) {
        const q = podSearchQuery.value.toLowerCase();
        list = list.filter((item) => {
          const code = (item.data.pod_code || item.id || "").toLowerCase();
          return code.includes(q) || String(item.id).toLowerCase().includes(q);
        });
      }

      if (showOnlyMissingPod.value) {
        list = list.filter((item) => hasMissingMonths(item.data));
      }

      return list;
    });

    const filteredPdrDetailsList = computed(() => {
      let list = detailsToList(
        rcuData.value?.pdr_analysis?.fatturabile_gas_details
      );

      if (pdrSearchQuery.value) {
        const q = pdrSearchQuery.value.toLowerCase();
        list = list.filter((item) => {
          const code = (item.data.pdr_code || item.id || "").toLowerCase();
          return code.includes(q) || String(item.id).toLowerCase().includes(q);
        });
      }

      if (showOnlyMissingPdr.value) {
        list = list.filter((item) => hasMissingMonths(item.data));
      }

      return list;
    });

    const pagedPodDetails = computed(() => {
      const start = podDetailsFirst.value;
      return filteredPodDetailsList.value.slice(
        start,
        start + podDetailsRows.value
      );
    });

    const pagedPdrDetails = computed(() => {
      const start = pdrDetailsFirst.value;
      return filteredPdrDetailsList.value.slice(
        start,
        start + pdrDetailsRows.value
      );
    });

    const resetPagination = () => {
      podsFirst.value = 0;
      pdrFirst.value = 0;
      podDetailsFirst.value = 0;
      pdrDetailsFirst.value = 0;
      expandedPods.value = {};
      expandedPdrs.value = {};
      monthsCompleteCache.clear();
    };

    const fetchMonthlyPresence = async () => {
      loadingMonthlyPresence.value = true;
      monthlyPresenceError.value = null;
      try {
        const response = await service.getRcuMonthlyPresence(18);
        if (!response.success) {
          throw new Error(
            response.message || "Errore nel recupero dello stato RCU"
          );
        }
        monthlyPresence.value = response.data?.months || [];
      } catch (error) {
        monthlyPresenceError.value =
          "Impossibile caricare lo stato dei file RCU mese per mese";
        monthlyPresence.value = [];
      } finally {
        loadingMonthlyPresence.value = false;
      }
    };

    const formatUploadDateTime = (value) => {
      if (!value) return "—";
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
    };

    const fetchRcuData = async () => {
      if (!selectedDate.value) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Seleziona una data prima di caricare i dati",
          life: 3000,
        });
        return;
      }

      loading.value = true;
      loadingDetails.value = false;
      resetPagination();

      const formattedDate = moment(selectedDate.value).format("YYYY-MM-01");

      try {
        // 1) Riepilogo veloce: la pagina diventa subito utilizzabile
        const summaryResponse = await service.checkRcuData(formattedDate, {
          details: false,
        });

        if (!summaryResponse.success) {
          throw new Error(
            summaryResponse.message || "Errore nel recupero dei dati"
          );
        }

        rcuData.value = summaryResponse.data;
        loading.value = false;

        toast.add({
          severity: "success",
          summary: "Successo",
          detail: `Riepilogo RCU caricato per ${moment(
            selectedDate.value
          ).format("MM/YYYY")}`,
          life: 2500,
        });

        // 2) Dettagli in background (non blocca il riepilogo)
        loadingDetails.value = true;
        try {
          const detailsResponse = await service.checkRcuData(formattedDate, {
            details: true,
            detailsOnly: true,
          });

          if (detailsResponse.success && rcuData.value) {
            rcuData.value = {
              ...rcuData.value,
              pod_analysis: {
                ...rcuData.value.pod_analysis,
                ...detailsResponse.data.pod_analysis,
              },
              pdr_analysis: {
                ...rcuData.value.pdr_analysis,
                ...detailsResponse.data.pdr_analysis,
              },
            };
          }
        } catch (detailsError) {
          toast.add({
            severity: "warn",
            summary: "Dettagli non disponibili",
            detail:
              "Il riepilogo è pronto, ma i dettagli POD/PDR non sono stati caricati. Riprova.",
            life: 6000,
          });
        } finally {
          loadingDetails.value = false;
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Errore durante il recupero dei dati RCU",
          life: 5000,
        });
        loading.value = false;
        loadingDetails.value = false;
      }
    };

    const getImportStatusClass = (status) => {
      switch (status) {
        case "IMPORTED":
          return "status-imported";
        case "NOT_IMPORTED":
          return "status-not-imported";
        case "ERROR":
          return "status-error";
        default:
          return "";
      }
    };

    const getSummaryStatusClass = (status) => {
      switch (status) {
        case "OK":
          return "status-ok";
        case "INCOMPLETE":
          return "status-incomplete";
        default:
          return "";
      }
    };

    const togglePod = (codPod) => {
      expandedPods.value[codPod] = !expandedPods.value[codPod];
    };

    const togglePdr = (codPdr) => {
      expandedPdrs.value[codPdr] = !expandedPdrs.value[codPdr];
    };

    const formatMonth = (monthStr) => {
      return moment(monthStr).format("MM/YYYY");
    };

    const formatPeriodRange = (monthsComplete) => {
      if (!monthsComplete || monthsComplete.length === 0) return "";
      const firstMonth = moment(monthsComplete[0].month).format("MM/YYYY");
      const lastMonth = moment(
        monthsComplete[monthsComplete.length - 1].month
      ).format("MM/YYYY");
      return firstMonth === lastMonth
        ? firstMonth
        : `${firstMonth} - ${lastMonth}`;
    };

    const getMonthStatusClass = (status) => {
      return status === "present" ? "present" : "missing";
    };

    const getMonthTooltip = (monthData, type = "Elettrico") => {
      const formattedMonth = moment(monthData.month).format("MMMM YYYY");
      const fType = type === "Gas" ? "fatturabile gas" : "fatturabile";
      return monthData.status === "present"
        ? `${formattedMonth}: Presente in ${fType}`
        : `${formattedMonth}: Mancante in ${fType}`;
    };

    const buildMonthsComplete = (monthsPresent) => {
      if (!monthsPresent || monthsPresent.length === 0) return [];

      const sorted = [...monthsPresent].sort();
      const presentSet = new Set(sorted);
      const first = moment(sorted[0], "YYYY-MM-DD").startOf("month");
      const last = moment(sorted[sorted.length - 1], "YYYY-MM-DD").startOf(
        "month"
      );
      const complete = [];
      const cursor = first.clone();

      while (cursor.isBefore(last, "month") || cursor.isSame(last, "month")) {
        const month = cursor.format("YYYY-MM-01");
        const isPresent = presentSet.has(month);
        complete.push({
          month,
          status: isPresent ? "present" : "missing",
          color: isPresent ? "green" : "red",
        });
        cursor.add(1, "month");
      }

      return complete;
    };

    const getMonthsComplete = (data) => {
      if (!data) return [];
      if (data.months_complete?.length) return data.months_complete;

      const monthsPresent = data.months_present || [];
      const cacheKey = monthsPresent.join("|");
      if (monthsCompleteCache.has(cacheKey)) {
        return monthsCompleteCache.get(cacheKey);
      }

      const complete = buildMonthsComplete(monthsPresent);
      monthsCompleteCache.set(cacheKey, complete);
      return complete;
    };

    const hasMissingMonths = (data) => {
      if (typeof data?.has_missing === "boolean") {
        return data.has_missing;
      }
      return getMonthsComplete(data).some(
        (monthData) => monthData.status === "missing"
      );
    };

    const toggleMissingFilter = (type) => {
      if (type === "pod") {
        showOnlyMissingPod.value = !showOnlyMissingPod.value;
        podDetailsFirst.value = 0;
      } else if (type === "pdr") {
        showOnlyMissingPdr.value = !showOnlyMissingPdr.value;
        pdrDetailsFirst.value = 0;
      }
    };

    onMounted(() => {
      fetchMonthlyPresence();
    });

    return {
      loading,
      loadingDetails,
      loadingMonthlyPresence,
      monthlyPresence,
      monthlyPresenceError,
      rcuData,
      podSearchQuery,
      pdrSearchQuery,
      expandedPods,
      expandedPdrs,
      selectedDate,
      showOnlyMissingPod,
      showOnlyMissingPdr,
      filteredPodDetailsList,
      filteredPdrDetailsList,
      pagedPodDetails,
      pagedPdrDetails,
      podsFirst,
      podsRows,
      pdrFirst,
      pdrRows,
      podDetailsFirst,
      podDetailsRows,
      pdrDetailsFirst,
      pdrDetailsRows,
      pagedActivePods,
      pagedActivePdrs,
      onPodsPage,
      onPdrPage,
      onPodDetailsPage,
      onPdrDetailsPage,
      onPodSearchInput,
      onPdrSearchInput,
      fetchRcuData,
      fetchMonthlyPresence,
      formatUploadDateTime,
      getImportStatusClass,
      getSummaryStatusClass,
      togglePod,
      togglePdr,
      toggleMissingFilter,
      formatMonth,
      formatPeriodRange,
      getMonthStatusClass,
      getMonthTooltip,
      hasMissingMonths,
      getMonthsComplete,
    };
  },
};
</script>

<style scoped>
.center-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.date-selection-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.upload-status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.upload-status-header .section-title {
  margin-bottom: 0;
}

.upload-status-hint {
  margin: 0 0 16px;
  color: #64748b;
  font-size: 0.9rem;
}

.upload-status-table-wrap {
  overflow-x: auto;
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.upload-status-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.upload-status-table th,
.upload-status-table td {
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.upload-status-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  font-weight: 600;
  color: #334155;
  z-index: 1;
}

.upload-status-table tbody tr:last-child td {
  border-bottom: none;
}

.presence-badge {
  display: inline-block;
  min-width: 42px;
  text-align: center;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
}

.presence-yes {
  background: #dcfce7;
  color: #166534;
}

.presence-no {
  background: #fee2e2;
  color: #991b1b;
}

.upload-datetime {
  color: #475569;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.date-selector-container {
  margin-top: 15px;
}

.date-selector-row {
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.date-selector-row label {
  white-space: nowrap;
  font-weight: bold;
  min-width: 160px;
  color: #333;
}

.custom-calendar {
  flex: 1;
  max-width: 200px;
}

.load-data-button {
  background: #04ba2e;
  border: 1px solid #04ba2e;
  padding: 10px 20px;
  white-space: nowrap;
}

.load-data-button:hover {
  background: #038f23;
  border: 1px solid #038f23;
}

.load-data-button:disabled {
  background: #6c757d;
  border: 1px solid #6c757d;
}

:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
}

:deep(.p-calendar .p-button) {
  background: #04ba2e;
  border: 1px solid #04ba2e;
}

:deep(.p-calendar .p-button:hover) {
  background: #038f23;
  border: 1px solid #038f23;
}

:deep(.month-picker-panel) {
  .p-datepicker-header {
    background: #04ba2e;
    color: white;
  }

  .p-datepicker-month {
    color: #333;
    font-weight: bold;
  }

  .p-highlight {
    background: #04ba2e !important;
    color: white !important;
  }
}

.page-header {
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.rcu-data-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #04ba2e;
  border-bottom: 2px solid #04ba2e;
  padding-bottom: 5px;
}

.info-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #1565c0;
}

.info-note i {
  color: #1976d2;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.info-item label {
  font-weight: bold;
  color: #666;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.summary-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #04ba2e, #038f23);
  border-radius: 8px;
  color: white;
}

.summary-number {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.summary-label {
  font-size: 12px;
  opacity: 0.9;
}

.tables-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.table-status-card {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

.table-header {
  background: #f8f9fa;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
}

.table-header h4 {
  margin: 0;
  font-size: 14px;
  color: #333;
}

.table-details {
  padding: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-row.error {
  flex-direction: column;
  background: #ffe6e6;
  padding: 8px;
  border-radius: 4px;
  margin-top: 8px;
}

.detail-label {
  font-weight: 500;
  color: #666;
}

.detail-value {
  color: #333;
}

.status-imported {
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
}

.status-not-imported {
  background: #ffc107;
  color: #333;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
}

.status-error {
  background: #dc3545;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
}

.status-ok {
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-incomplete {
  background: #ffc107;
  color: #333;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.pod-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.stat-item label {
  font-weight: bold;
  color: #666;
}

.pod-details-section {
  margin-top: 20px;
}

.details-loading {
  padding: 1rem;
  color: #64748b;
  font-style: italic;
}

.pod-details-section h4 {
  margin-bottom: 15px;
  color: #333;
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 15px;
}

.search-container {
  flex: 1;
}

.search-input {
  width: 100%;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.filter-container {
  flex-shrink: 0;
}

.filter-button {
  background: #6c757d;
  border: 1px solid #6c757d;
  color: white;
  transition: all 0.2s ease;
}

.filter-button:hover {
  background: #5a6268;
  border: 1px solid #5a6268;
}

.filter-button.filter-active {
  background: #dc3545;
  border: 1px solid #dc3545;
}

.filter-button.filter-active:hover {
  background: #c82333;
  border: 1px solid #c82333;
}

.pod-table-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.pod-table {
  width: 100%;
  border-collapse: collapse;
}

.pod-table th,
.pod-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.pod-table th {
  background: #04ba2e;
  color: white;
  font-weight: bold;
  position: sticky;
  top: 0;
}

.pod-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.pod-row:hover {
  background: #f5f5f5;
}

.pod-row.has-missing-months {
  background-color: #ffebee;
  border-left: 4px solid #dc3545;
}

.pod-row.has-missing-months:hover {
  background-color: #ffcdd2;
}

.toggle-icon {
  margin-right: 8px;
  color: #04ba2e;
  transition: transform 0.2s;
}

.months-detail-row {
  background: #f8f9fa;
}

.months-detail {
  padding: 15px;
}

.months-detail h6 {
  margin-bottom: 10px;
  color: #04ba2e;
  font-size: 14px;
}

.months-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.month-tag {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  margin: 2px;
  cursor: help;
  transition: all 0.2s ease;
}

.month-tag.present {
  background: #28a745;
  color: white;
}

.month-tag.missing {
  background: #dc3545;
  color: white;
}

.month-tag:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.legend {
  margin-top: 15px;
  display: flex;
  gap: 20px;
  align-items: center;
  font-size: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 6px;
  display: inline-block;
}

.legend-color.present {
  background: #28a745;
}

.legend-color.missing {
  background: #dc3545;
}

.no-data-text {
  color: #666;
  font-style: italic;
  margin: 10px 0;
  text-align: center;
}

.active-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.active-section {
  margin-bottom: 20px;
}

.active-section h4 {
  margin-bottom: 15px;
  color: #04ba2e;
  border-bottom: 2px solid #04ba2e;
  padding-bottom: 5px;
}

.active-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.active-item {
  padding: 15px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.active-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.active-item .code {
  font-weight: bold;
  color: #04ba2e;
  font-size: 1.1rem;
  margin-bottom: 8px;
}

.active-item .data-fine {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.no-active-data {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.error-message {
  background: #ffe6e6;
  color: #d63384;
  padding: 15px;
  border-radius: 4px;
  border-left: 4px solid #dc3545;
}

.actions-section {
  text-align: center;
  margin-top: 20px;
}

.refresh-button {
  background: #04ba2e;
  border: 1px solid #04ba2e;
  padding: 10px 20px;
}

.refresh-button:hover {
  background: #038f23;
  border: 1px solid #038f23;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

@media (max-width: 768px) {
  .info-grid,
  .summary-grid,
  .tables-container,
  .pod-stats {
    grid-template-columns: 1fr;
  }

  .summary-item {
    padding: 15px;
  }

  .summary-number {
    font-size: 24px;
  }

  .search-input {
    max-width: 100%;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-container {
    order: 1;
  }

  .filter-container {
    order: 2;
  }

  .filter-button {
    width: 100%;
    justify-content: center;
  }
}
</style>

